/*
 * "Scelti per te" (sections/for-you.liquid).
 *
 * 1. When the block gets close to the viewport, asks the CRM app
 *    (/apps/wishlist/for-you, app proxy) which products to show. Visitor id,
 *    recently viewed products and the session's last search (kept by
 *    browse-intent.js, at most 2 hours old) are sent only with tracking
 *    consent (Shopify Customer Privacy API), like the product-intent tracker.
 * 2. Renders the theme's own product cards in one request with the Section
 *    Rendering API (/search?q=id:1 OR id:2 …&section_id=for-you-grid|carousel).
 * 3. After 3 s without an answer shows the fallback collection, if set,
 *    otherwise hides the section. Fewer than 4 products: hidden.
 * 4. Tracks impressions and clicks (FOR_YOU_IMPRESSION / FOR_YOU_CLICK) through
 *    the tracker's /apps/intent/action, with consent only, and tags product
 *    links with sks_rec=for_you. The block's source travels as pickSource
 *    (the CRM stores its own `source` on every event) and the group
 *    (treated / control) lets integrated1 measure the uplift.
 */
(function () {
  "use strict";

  if (customElements.get("for-you-block")) return;

  //  The answer crosses Shopify's app proxy (about 0.5 s on its own) and
  //  the CRM app: 1.1-1.7 s measured on 29/09, so 800 ms hid the block for
  //  everyone. The request starts 1000px before the block is on screen, so
  //  the cards are usually in place before the visitor gets there, and the
  //  skeleton holds their room meanwhile.
  var TIMEOUT_MS = 3000;         // answer from the CRM app
  var CART_TIMEOUT_MS = 400;
  var VISITOR_COOKIE = "product_intent_visitor_id";
  var RECENTLY_VIEWED_KEY = "theme:recently-viewed-products";
  var LAST_SEARCH_KEY = "sks:last-search";          // written by browse-intent.js, with consent
  var LAST_SEARCH_TTL_MS = 2 * 60 * 60 * 1000;

  function privacy() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    function allowed(name) {
      try { return Boolean(api && typeof api[name] === "function" && api[name]()); } catch (e) { return false; }
    }
    return {
      analyticsProcessingAllowed: allowed("analyticsProcessingAllowed"),
      marketingAllowed: allowed("marketingAllowed"),
      preferencesProcessingAllowed: allowed("preferencesProcessingAllowed"),
      saleOfDataAllowed: allowed("saleOfDataAllowed"),
    };
  }

  function cookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : "";
  }

  function visitorId() {
    try {
      var tracker = window.IntegratedProductIntent;
      var context = tracker && typeof tracker.getBrowserContext === "function" ? tracker.getBrowserContext() : null;
      if (context && context.visitorId) return String(context.visitorId);
    } catch (e) { /* the tracker is optional */ }
    var id = cookie(VISITOR_COOKIE);
    return /^[A-Za-z0-9_-]{1,128}$/.test(id) ? id : "";
  }

  function recentlyViewed() {
    try {
      var ids = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
      return Array.isArray(ids) ? ids.map(String).filter(function (id) { return /^[0-9]+$/.test(id); }).slice(0, 10) : [];
    } catch (e) {
      return [];
    }
  }

  function lastSearch() {
    try {
      var last = JSON.parse(sessionStorage.getItem(LAST_SEARCH_KEY) || "null");
      return last && last.q && Date.now() - Number(last.at || 0) < LAST_SEARCH_TTL_MS ? String(last.q).slice(0, 80) : "";
    } catch (e) {
      return "";
    }
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error("timeout")); }, ms);
      promise.then(function (value) { clearTimeout(timer); resolve(value); }, function (error) { clearTimeout(timer); reject(error); });
    });
  }

  function cartProductIds() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (response) { return response.ok ? response.json() : { items: [] }; })
      .then(function (cart) { return (cart.items || []).map(function (item) { return String(item.product_id); }); })
      .catch(function () { return []; });
  }

  function trackingEndpoint() {
    var script = document.querySelector("script[data-api-base]");
    var base = script && script.getAttribute("data-api-base");
    return base ? base.replace(/\/+$/, "") + "/apps/intent/action" : "";
  }

  class ForYouBlock extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      var self = this;
      if (!("IntersectionObserver" in window)) return this.load();
      this._observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          self._observer.disconnect();
          self.load();
        }
      }, { rootMargin: "1000px 0px" });
      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._impressionObserver) this._impressionObserver.disconnect();
    }

    load() {
      var self = this;
      var status = privacy();
      this.tracking = status.analyticsProcessingAllowed || status.marketingAllowed;
      this.privacyStatus = status;
      this.visitor = this.tracking ? visitorId() : "";
      var cart = withTimeout(cartProductIds(), CART_TIMEOUT_MS).catch(function () { return []; });
      var ask = cart.then(function (cart) {
        var exclude = cart.slice(0, 20);
        if (self.dataset.productId) exclude.unshift(self.dataset.productId);
        var params = new URLSearchParams({
          limit: self.dataset.limit || "8",
          a: status.analyticsProcessingAllowed ? "1" : "0",
          m: status.marketingAllowed ? "1" : "0",
        });
        if (self.visitor) params.set("v", self.visitor);
        if (self.tracking) {
          var viewed = recentlyViewed();
          if (viewed.length) params.set("viewed", viewed.join(","));
          var query = lastSearch();
          if (query) params.set("q", query);
        }
        if (exclude.length) params.set("exclude", exclude.join(","));
        if (self.dataset.collection) params.set("collection", self.dataset.collection);
        if (self.dataset.audience) params.set("audience", self.dataset.audience);
        return withTimeout(fetch(self.dataset.endpoint + "?" + params.toString(), { credentials: "same-origin", headers: { Accept: "application/json" } }), TIMEOUT_MS)
          .then(function (response) { if (!response.ok) throw new Error("status " + response.status); return response.json(); });
      });
      ask
        .then(function (answer) {
          var items = (answer && answer.items) || [];
          if (answer && answer.disabled) return self.hide();
          if (items.length < 4) return self.fallback();
          self.answer = answer;
          return self.render(items, answer.source, answer.group);
        })
        .catch(function () { self.fallback(); });
    }

    render(items, source, group) {
      var self = this;
      var ids = items.map(function (item) { return "id:" + item.productId; }).join(" OR ");
      var section = this.dataset.layout === "grid" ? "for-you-grid" : "for-you-carousel";
      var url = this.dataset.searchUrl + "?type=product&q=" + encodeURIComponent(ids) + "&section_id=" + section;
      return withTimeout(fetch(url, { priority: "low" }), 4000)
        .then(function (response) { if (!response.ok) throw new Error("render " + response.status); return response.text(); })
        .then(function (html) {
          var list = new DOMParser().parseFromString(html, "text/html").querySelector("[data-for-you-list]");
          if (!list || Number(list.dataset.count || 0) < 4) return self.fallback();
          return self.insert(list, source, group);
        })
        .catch(function () { self.fallback(); });
    }

    insert(list, source, group) {
      var self = this;
      var carouselId = "for-you-carousel-" + this.dataset.sectionId;
      list.querySelectorAll('[id="for-you-carousel"]').forEach(function (el) { el.id = carouselId; });
      list.querySelectorAll('[aria-controls="for-you-carousel"]').forEach(function (el) { el.setAttribute("aria-controls", carouselId); });
      list.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
        try {
          var url = new URL(link.getAttribute("href"), window.location.origin);
          url.searchParams.set("sks_rec", "for_you");
          link.setAttribute("href", url.pathname + url.search + url.hash);
        } catch (e) { /* leave the link alone */ }
      });
      var ready = ["product-list", "scroll-carousel", "carousel-prev-button", "carousel-next-button"]
        .map(function (name) { return customElements.whenDefined(name); });
      return Promise.race([Promise.all(ready), new Promise(function (resolve) { setTimeout(resolve, 3000); })]).then(function () {
        var target = self.querySelector("[data-for-you-target]");
        target.replaceChildren(document.importNode(list, true));
        self.showReason(source);
        self.hidden = false;
        if (source) self.trackImpression(source, group);
        self.addEventListener("click", function (event) {
          var link = event.target.closest && event.target.closest('a[href*="/products/"]');
          if (link) self.trackClick(link, source, group);
        });
      });
    }

    fallback() {
      var template = this.querySelector("template[data-for-you-fallback]");
      if (!template) return this.hide();
      var list = template.content.querySelector("[data-for-you-list]");
      if (!list) return this.hide();
      return this.insert(list.cloneNode(true), null, null);
    }

    hide() {
      this.hidden = true;
    }

    showReason(source) {
      var reason = this.querySelector("[data-for-you-reason]");
      if (!reason) return;
      var text = source && this.dataset.showReason === "true" ? this.dataset["reason" + source.charAt(0).toUpperCase() + source.slice(1)] : "";
      reason.textContent = text || "";
      reason.hidden = !text;
    }

    send(payload) {
      if (!this.tracking) return;
      var endpoint = trackingEndpoint();
      if (!endpoint) return;
      var body = JSON.stringify(Object.assign({
        shopDomain: window.Shopify && window.Shopify.shop,
        visitorId: this.visitor || undefined,
        customerPrivacyStatus: this.privacyStatus,
        occurredAt: new Date().toISOString(),
      }, payload));
      try {
        if (navigator.sendBeacon && navigator.sendBeacon(endpoint, new Blob([body], { type: "text/plain" }))) return;
      } catch (e) { /* fall back to fetch */ }
      fetch(endpoint, { method: "POST", mode: "cors", keepalive: true, headers: { "Content-Type": "text/plain" }, body: body }).catch(function () {});
    }

    trackImpression(source, group) {
      var self = this;
      if (!this.tracking || !("IntersectionObserver" in window)) return;
      this._impressionObserver = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        self._impressionObserver.disconnect();
        var items = ((self.answer && self.answer.items) || []).map(function (item, index) {
          return { productId: item.productId, position: index + 1, source: item.source };
        });
        self.send({ actionType: "FOR_YOU_IMPRESSION", payload: { block: "for_you", pickSource: source, group: group, items: items, page: document.body.dataset.template || null } });
      }, { threshold: 0.5 });
      this._impressionObserver.observe(this);
    }

    trackClick(link, source, group) {
      var items = (this.answer && this.answer.items) || [];
      var handle = (link.getAttribute("href").match(/\/products\/([^/?#]+)/) || [])[1];
      var index = items.findIndex(function (item) { return item.handle === handle; });
      var item = index >= 0 ? items[index] : null;
      this.send({
        actionType: "FOR_YOU_CLICK",
        productId: item ? item.productId : undefined,
        productHandle: handle,
        payload: { block: "for_you", pickSource: source || "fallback", group: group, position: index + 1, itemSource: item ? item.source : null },
      });
    }
  }

  customElements.define("for-you-block", ForYouBlock);
})();
